import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { formatIDR } from "@/lib/format";
import { PageHeader, Loading } from "@/components/ui-helpers";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { DndContext, useDraggable, useDroppable, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";

function Card({ order }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: order.id });
  const style = transform ? { transform: `translate(${transform.x}px, ${transform.y}px)`, zIndex: 50 } : undefined;
  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes}
      className={`rounded-xl border border-border bg-card p-3 shadow-sm cursor-grab active:cursor-grabbing mb-2 ${isDragging ? "ring-2 ring-primary/40 opacity-90" : ""}`}
      data-testid="orders-kanban-card">
      <p className="font-heading text-sm font-medium">{order.order_number}</p>
      <p className="text-xs text-muted-foreground truncate">{order.customer_name}</p>
      <div className="flex items-center justify-between mt-2">
        <span className="font-mono text-xs">{formatIDR(order.order_total)}</span>
        <StatusBadge status={order.payment_status} />
      </div>
    </div>
  );
}

function Column({ status, orders }) {
  const { setNodeRef, isOver } = useDroppable({ id: status });
  return (
    <div ref={setNodeRef} className={`min-w-[280px] max-w-[300px] rounded-xl border border-border bg-secondary/40 flex flex-col ${isOver ? "bg-primary/5 ring-2 ring-primary/20" : ""}`} data-testid="orders-kanban-column">
      <div className="sticky top-0 px-3 py-2 border-b border-border flex items-center justify-between">
        <StatusBadge status={status} />
        <span className="text-xs text-muted-foreground font-medium">{orders.length}</span>
      </div>
      <div className="p-2 flex-1 min-h-[120px]">
        {orders.map((o) => <Card key={o.id} order={o} />)}
      </div>
    </div>
  );
}

export default function OrdersKanban() {
  const { constants, selectedTripId } = useApp();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [columns, setColumns] = useState({});
  const [loading, setLoading] = useState(true);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/orders-kanban", { params: { trip_id: selectedTripId } }); setColumns(r.data); }
    finally { setLoading(false); }
  }, [selectedTripId]);
  useEffect(() => { load(); }, [load]);

  const onDragEnd = async (event) => {
    const { active, over } = event;
    if (!over) return;
    const orderId = active.id;
    const newStatus = over.id;
    // find current
    let current = null;
    for (const [st, list] of Object.entries(columns)) { if (list.find((o) => o.id === orderId)) { current = st; break; } }
    if (current === newStatus) return;
    try {
      await api.post(`/orders/${orderId}/status`, { status: newStatus, note: "Dipindah via Kanban", override: isAdmin });
      toast.success(`Dipindah ke ${newStatus}`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal pindah status"); load(); }
  };

  const statuses = constants?.order_statuses || Object.keys(columns);

  return (
    <div>
      <PageHeader title="Papan Kanban Order" subtitle="Seret order untuk mengubah status">
        <Button variant="outline" onClick={() => navigate("/orders")}><ArrowLeft className="h-4 w-4 mr-1.5" />Kembali</Button>
      </PageHeader>
      {loading ? <Loading /> : (
        <DndContext sensors={sensors} onDragEnd={onDragEnd}>
          <div className="flex gap-3 overflow-x-auto pb-4">
            {statuses.map((st) => <Column key={st} status={st} orders={columns[st] || []} />)}
          </div>
        </DndContext>
      )}
    </div>
  );
}
